import {
  IInsertPrivateContentParams,
  IInsertPrivateNoteParams,
  INotesByTagsQuery,
} from '@rhizome/pgtypes';
import {
  Add,
  Dashboard,
  FilterAlt,
  HelpCenter,
  NavigateBefore,
  Public,
  Settings,
  Tag,
} from '@styled-icons/material';
import { useIsMobile } from 'apps/exegesis/styles/themes';
import { MobileTippy } from 'apps/exegesis/utils/components/MobileTippy';
import { logAppEvent } from 'apps/exegesis/utils/useAnalytics';
import { auth } from 'libs/shared-firebase/src';
import { NextRouter, useRouter } from 'next/router';
import React, { useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import styled from 'styled-components';
import { queryClient } from '../../pages/_app';
import { useMutationHook, usePaginatedQuery } from '../../utils/DataFetchers';
import { safeNanoid } from '../../utils/safeNanoid';
import { LoadingPulser } from '../StatusUI/LoadingPulser';
import { LoadingSpinner } from '../StatusUI/LoadingSpinner';
import { CollapseButton, useMenu } from './NotesLayout';
import PageFilters from './PageFilters';
import SidebarNote from './SidebarNote';
const SidebarNoteDisplay = (props: {
  menuOpen: boolean;
  setMenuOpen: (newMenuState: boolean) => any;
}) => {
  const [loadingMore, setLoadingMore] = useState(false);
  const [published, setPublished] = useState(false as boolean);
  const [titleQuery, setTitleQuery] = useState(null as string | null);
  const [selectedTags, setSelectedTags] = useState(new Set() as Set<string>);

  const [filterOpen, setFilterOpen] = useState(false);
  const [user] = useAuthState(auth);
  const {
    data: notes,
    status: notesStatus,
    onScroll,
    containerRef,
  } = usePaginatedQuery<INotesByTagsQuery>(
    [
      'NotesByTags',
      {
        tags: Array.from(selectedTags),
        public: published ? published : null,
        author: auth?.currentUser?.uid,
        titleSearch: titleQuery && titleQuery.length > 0 ? titleQuery : null,
        limit: true,
      },
    ],
    { paginateOn: 'creation_date' }
  );

  const { mutateAsync: createNote } = useMutationHook<{
    params: [IInsertPrivateNoteParams, IInsertPrivateContentParams];
    result: void;
  }>({
    route: 'CreateNote',
  });

  const router = useRouter();
  const isMobile = useIsMobile();
  const setMenuOpen = useMenu((state) => state.setMenuOpen);

  return (
    <>
      <NavContainer>
        <div>
          <NewNoteButton
            onClick={() => {
              if (isMobile) {
                setMenuOpen(false);
              }
              newNote(createNote, user?.uid, router);
            }}
          >
            <MobileTippy
              placement="bottom"
              arrow={false}
              offset={[0, 0]}
              content="new note"
            >
              <Add height="20px" />
            </MobileTippy>
          </NewNoteButton>
          <FilterButton
            selected={filterOpen}
            onClick={() => {
              setFilterOpen(!filterOpen);
            }}
          >
            <MobileTippy
              placement="bottom"
              arrow={false}
              offset={[0, 0]}
              content={filterOpen ? 'close filters' : 'open note filters'}
            >
              <FilterAlt height="20px" />
            </MobileTippy>
          </FilterButton>
          <TopMenuButton preview="dashboard" href="/notes/dashboard">
            <Dashboard height="20px" />
          </TopMenuButton>
          <TopMenuButton preview="tag browser" href="/notes/tags">
            <Tag height="20px" />
          </TopMenuButton>
          <TopMenuButton preview="settings" href="/notes/settings">
            <Settings height="20px" />
          </TopMenuButton>
          <TopMenuButton preview="help" href="/notes/help">
            <HelpCenter height="20px" />
          </TopMenuButton>
          <TopMenuButton preview="profile" href="/notes/profile">
            <Public height="20px" />
          </TopMenuButton>

          <CollapseButton
            open={props.menuOpen}
            onClick={() => {
              props.setMenuOpen(false);
            }}
          >
            <NavigateBefore height="20px" />
          </CollapseButton>
        </div>
        <PageFilters
          published={published}
          setPublished={setPublished}
          title={titleQuery}
          setTitle={setTitleQuery}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          open={filterOpen}
        />
      </NavContainer>
      {notesStatus === 'loading' ? (
        <LoadingPulser />
      ) : (
        <NotesContainer onScroll={onScroll} ref={containerRef}>
          <div style={{ minHeight: '110%' }}>
            {notes?.pages?.map((page) => {
              return page.map((note) => {
                return (
                  <SidebarNote
                    public={false}
                    key={note.note_id}
                    noteId={note.note_id}
                    title={note.title}
                    creationDate={note.creation_date}
                    lastEditedDate={note.last_edited_date}
                  />
                );
              });
            })}
          </div>
          {loadingMore ? (
            <LoadingSpinner
              width={50}
              style={{ marginTop: '10px', marginBottom: '20px' }}
            />
          ) : null}
        </NotesContainer>
      )}
    </>
  );
};

const TopMenuButton = (props: {
  href: string;
  children: any;
  preview: string;
}) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const setMenuOpen = useMenu((state) => state.setMenuOpen);
  return (
    <span
      onClick={() => {
        if (isMobile) {
          setMenuOpen(false);
        }
        router.push(props.href);
      }}
    >
      {isMobile ? (
        <NavLink selected={router.pathname === props.href}>
          {props.children}
        </NavLink>
      ) : (
        <MobileTippy
          placement="bottom"
          arrow={false}
          offset={[0, 0]}
          content={props.preview}
        >
          <NavLink selected={router.pathname === props.href}>
            {props.children}
          </NavLink>
        </MobileTippy>
      )}
    </span>
  );
};

const newNote = (
  createNote: (
    param: [IInsertPrivateNoteParams, IInsertPrivateContentParams]
  ) => any,
  user: string,
  router: NextRouter
) => {
  logAppEvent('note_created');
  const new_note_id = safeNanoid();
  const block_id = safeNanoid();

  const newNote = {
    note_id: new_note_id,
    author: user,
    creation_date: new Date(),
    last_edited_date: new Date(),
    title: null,
    tags: [],
    starred: false,
    public: false,
    type: 'slate',
  };

  const emptyBlock = {
    slate_content: {
      type: 'p',
      id: block_id,
      children: [
        {
          text: '',
        },
      ],
    },
    slate_parent: new_note_id,
    slate_index: 0,
    block_id,
    creation_date: new Date(),
    last_edited_date: new Date(),
    tags: [],
    fulltext: '' as never,
    text_title: null,
    text_body: null,
    author: null,
    content_type: 'slate',
    image_url: null,
    url_url: null,
    url_site: null,
    url_media: null,
    slide_content: null,
  };

  createNote([newNote, emptyBlock]).then(() => {
    queryClient.invalidateQueries(['NotesByTags']);
    router.push('/notes/editor/' + new_note_id);
  });
};

const NavContainer = styled.div`
  width: 100%;
  padding: 10px 5px 5px 10px;
  border-bottom: 1px solid grey;
`;

const NotesContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  animation-name: fadeIn;
  animation-duration: 0.7s;
`;

const NavLink = styled.a<{ selected: boolean }>`
  border-radius: 0.2rem;
  display: inline-block;
  padding: 2px;
  margin: 3px;
  color: rgb(68, 68, 68);
  background-color: ${(props) =>
    props.selected ? props.theme.colors.main.medium : 'none'};

  :hover {
    cursor: pointer;
  }
`;

const NewNoteButton = styled.button`
  background-color: transparent;
  display: inline-block;
  padding: 2px;
  margin: 3px;
  color: rgb(68, 68, 68);
  border: none;

  :hover {
    cursor: pointer;
  }
`;

const FilterButton = styled.button<{ selected: boolean }>`
  background-color: ${(props) =>
    props.selected ? props.theme.colors.main.medium : 'transparent'};
  border: none;
  padding: 2px;
  margin: 3px;
  color: rgb(68, 68, 68);
  :hover {
    cursor: pointer;
  }
`;

export default SidebarNoteDisplay;
